import { logger } from '@beelday/common'
import { useCallback, useEffect, useState } from 'react'
import { AudioOutput, audioOutputsOf } from './model/speakers'

const log = logger.create('presenter-mode')

/**
 * Whether this browser can send a player's sound to a chosen output:
 * `setSinkId` on media elements (Chrome, Edge; not Safari) and a list of
 * devices to choose from.
 */
export const canChooseSpeakers = (): boolean =>
	typeof HTMLMediaElement !== 'undefined' &&
	typeof HTMLMediaElement.prototype.setSinkId === 'function' &&
	typeof navigator.mediaDevices?.enumerateDevices === 'function'

export type AudioOutputs = {
	/** What is plugged in; `null` until the browser has listed it. */
	outputs: ReadonlyArray<AudioOutput> | null
	/**
	 * Asks the browser for audio access - which reveals the devices' names -
	 * and lists them again. The microphone it opens is stopped at once.
	 */
	revealNames: () => Promise<void>
}

/**
 * The audio outputs of this computer, kept up to date as speakers are
 * plugged in and out (`devicechange`). The hardware check's
 * `useMediaDevices` is not reused: it lists devices only after its own
 * camera and microphone test, selects the first speaker for the user, and
 * touches `localStorage` and `navigator.mediaDevices` when imported.
 */
export const useAudioOutputs = (): AudioOutputs => {
	const [outputs, setOutputs] = useState<ReadonlyArray<AudioOutput> | null>(
		null
	)
	// Bumped to list again; the effect below depends on it.
	const [listing, setListing] = useState(0)

	useEffect(() => {
		const devices = navigator.mediaDevices
		if (typeof devices?.enumerateDevices !== 'function') {
			return
		}
		let live = true
		const list = (): void => {
			devices.enumerateDevices().then(
				all => {
					if (live) {
						setOutputs(audioOutputsOf(all))
					}
				},
				error => log.warn('The audio outputs cannot be listed', error)
			)
		}
		list()
		devices.addEventListener('devicechange', list)
		return () => {
			live = false
			devices.removeEventListener('devicechange', list)
		}
	}, [listing])

	const revealNames = useCallback(async (): Promise<void> => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
			stream.getTracks().forEach(track => track.stop())
		} catch (error) {
			log.warn(
				'Audio access was not given, the speaker names stay hidden',
				error
			)
			return
		}
		setListing(count => count + 1)
	}, [])

	return { outputs, revealNames }
}
